import React, { Suspense, lazy, useEffect, useLayoutEffect, useRef, useState } from 'react';
import tokens from '../design/tokens';
import { TAGLINE, noOrphan } from '../brand';
// Own chunk: the 198 KB drawing loads only on the homepage, never for portal users.
const loadFacility = () => import('./facility/ByrdsenseFacility');
const ByrdsenseFacility = lazy(loadFacility);

/**
 * Byrdsense public homepage.
 *
 * Built to design/mockups/homepage.mockup.html — same layout, spacing, type,
 * colors, copy and hero chart. Public pages render LIGHT ONLY: no dark-mode
 * toggle (the toggle lives in the customer portal, not here).
 *
 * The visual rule is "the reading, not the machine": ivory ground, navy ink,
 * hairline rules, mono for every number, and saturated color ONLY where a motor
 * is telling you something.
 */

const C = tokens.color;
const F = tokens.font;
const R = tokens.radius;

// Homepage-only text colours (Batch A2, 2026-10-02): darker than the shared
// tokens so supporting text reads comfortably for plant staff. Measured on
// ivory #F6F4EE: TEXT 9.4:1 (token text 7.7:1), MUTED 6.9:1 (token textMuted
// 4.6:1). tokens.js is shared with the portal and the onboarding app, so the
// tokens themselves are unchanged.
const TEXT = '#2F405F';
const MUTED = '#45556F';

const MONO = { fontFamily: F.mono };
const DISP = { fontFamily: F.display };

const CONTACT_API = 'https://slrai28hb2.execute-api.us-east-1.amazonaws.com/prod/reports/contact';

// Job #97818 (bench job b5598d63, PRE_REPAIR bench capture, 2026-03-23).
// Every value in the card's TOP section is measured on that capture — verified
// 2026-09-27 against ByrdSenseTelemetry, partition job_b5598d63-…, 16:55Z:
//   BENCH-DE  overallRmsVelMmSec 3.15, overallRmsAccG 1.237
//   BENCH-NDE overallRmsVelMmSec 2.85, overallRmsAccG 0.58
// The ratio is the ACCELERATION ratio, 1.237 / 0.58 = 2.13 -> "2.1×" (the
// velocity ratio is only 1.1). HP and RPM are the job record's nameplate
// (motorHp 50, motorRpm 3535). The pill quotes the job record's complaint
// ("Bearing noise de"). No teardown finding is recorded for this job, so none
// is shown — the teardown finding is unverified. The 30-day chart BELOW is not
// from this job — it is labelled as an illustration of what monitoring would
// have shown, and ends at the measured values.
export const JOB = {
  hp: 50,
  rpm: 3535,
  deVelocity: '3.15',
  ndeVelocity: '2.85',
  deAccel: '1.24 g',
  accelRatio: '2.1×',
};

export default function HomePage() {
  const [facilityReady, setFacilityReady] = useState(false);
  useScrollToHashOnLoad(facilityReady);
  // If the chunk fails, the section never mounts — don't hold the scroll for it.
  useEffect(() => { loadFacility().catch(() => setFacilityReady(true)); }, []);
  return (
    <div style={{ position: 'relative', background: C.ground, color: C.ink, fontFamily: F.body, minHeight: '100vh', overflowX: 'hidden' }}>
      <ResponsiveStyles />
      <InstrumentGrid />
      <Nav />
      <Hero />
      {/* Fictional sample plant — kept apart from the Hero's real bench card. */}
      {/* .bs-shell = the hero's container, so both share left/right edges. */}
      <div className="bs-shell">
        <Suspense fallback={null}>
          <ByrdsenseFacility onReady={() => setFacilityReady(true)} />
        </Suspense>
      </div>
      <TheLoop />
      <Services />
      <ServiceSpec />
      <ReportPreview />
      <RequestBand />
      <SiteFooter />
    </div>
  );
}

/* Direct links (/#request, /#how, …) must land on their section. This page is
   client-rendered, so the browser's own jump to the fragment happens before
   the target exists — on live job 50 a /#request link stayed at the top
   (checked 2026-10-01). Scroll once, after the lazy facility section has
   mounted and the web fonts have loaded, since both move everything below
   them. Never hijack a visitor who has already scrolled. */
function useScrollToHashOnLoad(ready) {
  const done = useRef(false);
  useEffect(() => {
    if (!ready || done.current) return undefined;
    let id = '';
    try { id = decodeURIComponent(window.location.hash.slice(1)); } catch { return undefined; }
    if (!id) return undefined;
    // `done` is set inside the frame, so a cancelled run (unmount before the
    // fonts settle) never blocks a later one.
    let cancelled = false;
    const fonts = document.fonts?.ready ?? Promise.resolve();
    fonts.then(() => requestAnimationFrame(() => {
      if (cancelled || done.current) return;
      done.current = true;
      const target = document.getElementById(id);
      if (!target || window.scrollY > 0) return;
      target.scrollIntoView();
    }));
    return () => { cancelled = true; };
  }, [ready]);
}

/* ── Responsive rules ──────────────────────────────────────────────────────
   Breakpoints per the brief: hero stacks below 1024 with the instrument panel
   full width; the five-step loop becomes a vertical list below 768; service
   cards go 3 -> 1 (never 2+1). */
function ResponsiveStyles() {
  return (
    <style>{`
      .bs-shell { padding-left: 72px; padding-right: 72px; }
      .bs-hero { display: grid; grid-template-columns: 540px minmax(0,1fr); gap: 56px; align-items: start; }
      .bs-loop { display: grid; grid-template-columns: repeat(5, minmax(0,1fr)); gap: 0; position: relative; }
      .bs-services { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 20px; }
      .bs-spec-row { display: grid; grid-template-columns: 260px minmax(0,1fr); gap: 24px; padding: 14px 0; align-items: baseline; }
      .bs-report { display: grid; grid-template-columns: minmax(0,1fr) 500px; gap: 40px; align-items: center; }
      .bs-request { display: grid; grid-template-columns: minmax(0,1fr) 420px; gap: 48px; align-items: center; }
      .bs-navlinks { display: flex; align-items: center; gap: 36px; }
      .bs-cta-short { display: none; }
      /* 62px, not 68: "Straight answers." is 571px at 68 in the 540px hero
         column (measured in Chrome, 2026-10-01); 521px at 62. */
      .bs-h1 { font-size: 62px; }
      /* Exactly two lines (Pete, 2026-10-01): one line per span; no wrapping
         inside a line from 768px up, where each size below is measured to fit. */
      .bs-h1-line { display: block; }
      @media (min-width: 768px) { .bs-h1-line { white-space: nowrap; } }
      .bs-stats { display: flex; gap: 36px; }

      @media (max-width: 1279px) {
        .bs-shell { padding-left: 40px; padding-right: 40px; }
        .bs-h1 { font-size: 56px; }
        .bs-navlinks { gap: 22px; }
        /* The hero panel is under 600px wide here: readouts 2x2 at full size. */
        .bs-readouts { grid-template-columns: repeat(2, minmax(0,1fr)) !important; gap: 18px 14px !important; }
      }
      @media (max-width: 1023px) {
        .bs-hero { grid-template-columns: minmax(0,1fr); gap: 40px; }
        .bs-readouts { grid-template-columns: repeat(4, minmax(0,1fr)) !important; }
        .bs-report { grid-template-columns: minmax(0,1fr); gap: 28px; }
        .bs-request { grid-template-columns: minmax(0,1fr); gap: 32px; }
        .bs-services { grid-template-columns: minmax(0,1fr); }
        .bs-spec-row { grid-template-columns: minmax(0,1fr); gap: 4px; }
        .bs-h1 { font-size: 50px; }
        .bs-navlinks .bs-navlink { display: none; }
        /* ...and give the header a way back in. */
        .bs-navlinks .bs-navmenu { display: inline-flex !important; }
        /* Five columns cannot hold readable step text below 1024px: a vertical
           list, circle beside the words. */
        .bs-loop { grid-template-columns: minmax(0,1fr); gap: 28px; max-width: 680px; }
        .bs-loop-rail { display: none !important; }
        .bs-loop-step { flex-direction: row !important; align-items: flex-start; gap: 20px !important; padding-right: 0 !important; }
        .bs-loop-circle { width: 64px !important; height: 64px !important; }
      }
      @media (max-width: 767px) {
        .bs-shell { padding-left: 20px; padding-right: 20px; }
        /* The 4-up readout row cannot fit 390px; go 2x2. */
        .bs-readouts { grid-template-columns: repeat(2, minmax(0,1fr)) !important; gap: 18px 14px !important; }
        .bs-panel { padding: 20px 16px 18px !important; border-radius: 14px !important; }
        .bs-panel-head { flex-direction: column; align-items: flex-start !important; gap: 12px !important; }
        .bs-pills { width: 100%; }
        .bs-report { padding: 28px 20px !important; }
        .bs-request { padding: 40px 20px !important; }
        .bs-service { padding: 24px !important; }
        /* Scales with the phone so "Straight answers." (8.4 x the font size
           wide) stays one line down to 320px; 40px cap from ~390px up. */
        .bs-h1 { font-size: min(40px, calc((100vw - 40px) / 8.6)); }
        .bs-stats { flex-direction: column; gap: 18px; }
        .bs-hero-cta { flex-direction: column; align-items: stretch; }
        .bs-hero-cta > a { justify-content: center; }
        /* 150px is the floor for the wordmark; below it, use the icon. */
        .bs-wordmark { width: 150px !important; }
        /* At 390px the row is wordmark + Menu + CTA, and the full CTA label
           does not fit: it pushed the Menu button onto the wordmark. Shorten
           the label — the menu carries the rest of the nav. */
        .bs-navcta { padding: 10px 14px !important; font-size: 15px; }
        .bs-navlinks { gap: 10px !important; }
        .bs-navmenu { padding: 0 12px !important; font-size: 15px !important; }
        .bs-cta-long { display: none; }
        .bs-cta-short { display: inline; }
      }
      @media (prefers-reduced-motion: reduce) { * { animation: none !important; transition: none !important; } }
      a:focus-visible, button:focus-visible, input:focus-visible, textarea:focus-visible {
        outline: 2px solid ${C.primary}; outline-offset: 2px;
      }
    `}</style>
  );
}

/* Faint 40px instrument grid on the ivory ground, 4.5% navy. */
function InstrumentGrid() {
  return (
    <svg aria-hidden="true" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
      <defs>
        <pattern id="bs-grid" width={tokens.layout.gridSize} height={tokens.layout.gridSize} patternUnits="userSpaceOnUse">
          <path d={`M${tokens.layout.gridSize} 0H0V${tokens.layout.gridSize}`} fill="none" stroke={C.ink} strokeOpacity="0.045" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#bs-grid)" />
    </svg>
  );
}

// The wordmark's B is the icon — never place the icon beside it.
const WORDMARK = '/assets/brand/byrdsense-wordmark-color.svg';
const WORDMARK_ON_NAVY = '/assets/brand/byrdsense-wordmark-reversed.svg';

/* ── NAV ─────────────────────────────────────────────────────────────────── */
const NAV_LINKS = [
  ['#how', 'How it works'],
  ['#screening', 'Screening'],
  ['#monitoring', 'Monitoring'],
  ['/login', 'Customer login'],
];

function Nav() {
  const link = { color: C.ink, textDecoration: 'none' };
  // Below 1024px the inline links are hidden by .bs-navlink { display: none }.
  // Without this button they were simply gone on a phone — the whole site was
  // unreachable from the header except the CTA. Plain button, no animation.
  const [open, setOpen] = useState(false);
  return (
    <div className="bs-shell" style={{ position: 'relative', height: 84, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${C.hairline}` }}>
      <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', minWidth: 0, flexShrink: 1, overflow: 'hidden' }} aria-label="Byrdsense home">
        <img className="bs-wordmark" src={WORDMARK} alt="Byrdsense" style={{ width: 180, height: 'auto', display: 'block' }} />
      </a>
      <div className="bs-navlinks" style={{ fontSize: 16, fontWeight: 500, flexShrink: 0 }}>
        {NAV_LINKS.map(([href, label]) => (
          <a key={href} className="bs-navlink" href={href} style={link}>{label}</a>
        ))}
        <button
          type="button"
          className="bs-navmenu"
          aria-expanded={open}
          aria-controls="bs-mobile-nav"
          onClick={() => setOpen((v) => !v)}
          style={{ display: 'none', alignItems: 'center', gap: 8, height: 42, padding: '0 14px', background: 'transparent', color: C.ink, border: `1px solid ${C.hairline}`, borderRadius: R.base, fontFamily: F.body, fontSize: 16, fontWeight: 600, cursor: 'pointer' }}
        >
          {open ? 'Close' : 'Menu'}
        </button>
        <a className="bs-navcta" href="#request" style={{ color: '#FFFFFF', background: C.ink, textDecoration: 'none', padding: '12px 20px', borderRadius: R.base, fontWeight: 600, whiteSpace: 'nowrap' }}>
          <span className="bs-cta-long">Request a screening</span>
          <span className="bs-cta-short">Request</span>
        </a>
      </div>

      {open && (
        <div
          id="bs-mobile-nav"
          className="bs-mobilenav"
          style={{ position: 'absolute', top: 84, left: 0, right: 0, zIndex: 40, background: C.surface, borderBottom: `1px solid ${C.hairline}`, boxShadow: '0 10px 24px rgba(10,27,59,0.10)', padding: '10px 0' }}
        >
          {NAV_LINKS.map(([href, label]) => (
            <a
              key={href}
              href={href}
              onClick={() => setOpen(false)}
              style={{ ...link, display: 'block', padding: '14px 24px', fontSize: 16, fontWeight: 500, borderBottom: `1px solid ${C.hairlineSoft}` }}
            >
              {label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── HERO ────────────────────────────────────────────────────────────────── */
function Hero() {
  const stats = [
    ['Hourly', 'readings from every asset'],
    ['A–F', 'one grade per asset, ISO 20816'],
    ['A call', 'typically the same business day'],
  ];
  // Hero text starts near the nav, level with the top of the chart panel (Pete, 2026-10-10).
  return (
    <div className="bs-shell bs-hero" style={{ position: 'relative', paddingTop: 40, paddingBottom: 56 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
        <div style={{ ...MONO, fontSize: 14, fontWeight: 500, letterSpacing: '0.06em', color: C.link }}>
          {/* The tagline (Pete, 2026-10-10). Two halves that never break inside, so a phone wraps it cleanly onto two lines. */}
          <span style={{ whiteSpace: 'nowrap' }}>INDUSTRIAL EQUIPMENT MONITORING</span>{' '}<span style={{ whiteSpace: 'nowrap' }}>WITH A PHONE CALL</span>
        </div>
        <h1 className="bs-h1" style={{ margin: 0, ...DISP, fontWeight: 800, lineHeight: 1.0, letterSpacing: F.tracking.tightest, color: C.ink }}>
          <span className="bs-h1-line">Smart sensors.</span>
          <span className="bs-h1-line">Straight answers.</span>
        </h1>
        <p style={{ margin: 0, fontSize: 19, lineHeight: 1.6, color: TEXT, maxWidth: 500 }}>
          Sensors on the equipment your plant can't run without. Readings every hour, graded against ISO 20816 (formerly ISO 10816). We review them every business day, and if equipment crosses a limit, we call you — typically the same business day.
        </p>
        {/* Service region (Pete, 2026-10-10) — moved out of the eyebrow, which is now the tagline. */}
        <div style={{ fontSize: 16, fontWeight: 500, color: MUTED }}>Serving Southeast Tennessee and North{'\u00a0'}Georgia</div>
        <div className="bs-hero-cta" style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <a href="#request" style={{ display: 'inline-flex', alignItems: 'center', height: 52, padding: '0 24px', background: C.ink, color: '#FFFFFF', borderRadius: R.md, fontWeight: 600, fontSize: 16, textDecoration: 'none' }}>Request a screening</a>
          <a href="#how" style={{ display: 'inline-flex', alignItems: 'center', height: 52, padding: '0 24px', border: `1.5px solid ${C.hairlineStrong}`, color: C.ink, borderRadius: R.md, fontWeight: 600, fontSize: 16, textDecoration: 'none' }}>See how it works</a>
        </div>
        <div className="bs-stats" style={{ paddingTop: 18, borderTop: `1px solid ${C.hairline}`, marginTop: 6 }}>
          {stats.map(([n, l]) => (
            <div key={n} style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 170 }}>
              <div style={{ ...DISP, fontWeight: 700, fontSize: 28, color: C.ink }}>{n}</div>
              <div style={{ fontSize: 16, lineHeight: 1.4, color: MUTED }}>{l}</div>
            </div>
          ))}
        </div>
      </div>
      <InstrumentPanel />
    </div>
  );
}

/* ── INSTRUMENT PANEL — the hero chart ───────────────────────────────────── */
function InstrumentPanel() {
  return (
    <div className="bs-panel" style={{ position: 'relative', background: C.surface, border: `1px solid ${C.hairline}`, borderRadius: R['3xl'], boxShadow: tokens.shadow.panel, padding: '28px 28px 24px', display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
      {/* TOP — measured on Job #97818 (a 50 HP motor). Nothing here is illustrative. */}
      <div className="bs-panel-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        {/* Pete's copy, word for word (2026-10-10); replaces the job pill and the HP/RPM line. */}
        <div style={{ fontSize: 17, fontWeight: 600, lineHeight: 1.4, color: C.ink }}>{BENCH_LINE}</div>
        <div className="bs-pills" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* Status pill — ISO semantic color. DE 3.15 mm/s on a 50 HP motor is grade C. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderRadius: R.full, background: C.statusBg.watch, color: C.status.watch, fontWeight: 600, fontSize: 15, whiteSpace: 'nowrap' }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: C.status.watchStroke, display: 'inline-block' }} />C · Watch
          </div>
          {/* Not a status pill — brand blue, so it never reads as an ISO grade. */}
          <div style={{ padding: '9px 14px', borderRadius: R.full, border: `1px solid ${C.primary}`, color: C.link, fontWeight: 600, fontSize: 15, whiteSpace: 'nowrap' }}>
            DE bearing noise reported
          </div>
        </div>
      </div>

      <div className="bs-readouts" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 14 }}>
        <Readout label="DE VELOCITY" value={JOB.deVelocity} unit="mm/s" color={C.status.watch} />
        <Readout label="NDE VELOCITY" value={JOB.ndeVelocity} unit="mm/s" />
        <Readout label="DE ACCEL" value={JOB.deAccel.split(' ')[0]} unit={JOB.deAccel.split(' ')[1]} />
        <Readout label="DE / NDE ACCEL" value={JOB.accelRatio} color={C.status.attention} />
      </div>

      {/* BELOW — an illustration, not this job's data. Its tag mirrors the
          "Measured" tag above, dashed, so the two halves read as different
          kinds of thing. Headline + subtitle: Pete's copy (2026-10-10). */}
      <div style={{ borderTop: `1px solid ${C.hairlineStrong}`, paddingTop: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ ...MONO, alignSelf: 'flex-start', fontSize: 13, fontWeight: 500, color: MUTED, border: `1px dashed ${C.hairlineStrong}`, borderRadius: R.full, padding: '3px 10px' }}>
          {CHART_TAG}
        </span>
        <div style={{ ...DISP, fontWeight: 700, fontSize: 22, lineHeight: 1.25, color: C.ink }}>{CHART_HEADLINE}</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: MUTED }}>{CHART_SUBTITLE}</div>
      </div>

      <TrendChart />
    </div>
  );
}

function Readout({ label, value, unit, color }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <div style={{ ...MONO, fontSize: 14, letterSpacing: '0.03em', color: MUTED, whiteSpace: 'nowrap' }}>{label}</div>
      <div style={{ ...MONO, fontSize: 28, fontWeight: 500, lineHeight: 1.15, color: color || C.ink, whiteSpace: 'nowrap' }}>
        {/* A real space before the unit: "1.24 g", "3.15 mm/s" — as text, not a margin. */}
        {value}{unit && <span style={{ fontSize: 16, color: MUTED }}>{' '}{unit}</span>}
      </div>
    </div>
  );
}

// The chart's trend, in the original 700x300 drawing space (y=240 is 0 mm/s,
// y=30 is 4.0): v = (240 - y) / 52.5. One knot every 2 days, Day 1 to 30...
const DE_Y = [170, 169, 168, 166, 163, 160, 156, 150, 144, 136, 126, 116, 106, 96, 86];
const NDE_Y = [176, 176, 175, 174, 172, 170, 167, 163, 158, 151, 142, 132, 121, 110, 100];
const toMmS = (y) => (240 - y) / 52.5;
// ...through the bench values at Day 30 (DE 3.15, NDE 2.85, Job #97818), then
// on to Day 36 — "if ignored" (Pete, 2026-10-10): DE keeps climbing toward
// the top of the chart, NDE stays below it.
export const DE_TREND = [...DE_Y.map(toMmS), 3.15, 3.33, 3.53, 3.75];
export const NDE_TREND = [...NDE_Y.map(toMmS), 2.85, 2.95, 3.06, 3.18];

// Hourly readings around that trend (Pete, 2026-10-10): jagged like real
// hourly vibration, moderate noise — DE within ±0.15 mm/s, NDE a bit less
// (±0.10) — from a FIXED seed, so the chart is the same on every load. Still
// an illustration, labelled as one. Rules the series keeps (tests check them):
// DE crosses the B/C line (2.8) exactly once and stays above it; dot 1 is
// that first crossing; the trend passes 3.15 / 2.85 at Day 30; NDE stays
// below DE after Day 30; nothing dips into zone A or off the top.
export const HOURS = 864; // 36 days
export const DAY30_HOUR = 719;
const DAYS = 36;
const ISO_BC = 2.8, ISO_AB_FLOOR = 1.13, SEED = 1010, CROSS_STEP = 0.12;
function mulberry32(a) {
  return () => {
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
// Knot k sits at day 2k (hour k * 47.9); Day 30 = knot 15 = hour ~719.
export function trendAt(knots, h) {
  const p = (h * (knots.length - 1)) / (HOURS - 1);
  const i = Math.min(knots.length - 2, Math.floor(p));
  return knots[i] + (knots[i + 1] - knots[i]) * (p - i);
}
function hourly(knots, sd, cap, rand, limit) {
  const gauss = () => Math.sqrt(-2 * Math.log(1 - rand())) * Math.cos(2 * Math.PI * rand());
  const cross = limit ? Array.from({ length: HOURS }, (_, h) => h).find((h) => trendAt(knots, h) >= limit) : HOURS;
  const out = [];
  let ar = 0;
  for (let h = 0; h < HOURS; h++) {
    ar = 0.5 * ar + sd * 0.866 * gauss(); // AR(1): a little hour-to-hour memory
    let n = Math.max(-cap, Math.min(cap, ar));
    let centre = trendAt(knots, h);
    if (limit) {
      // One crossing, no ceiling (Pete, 2026-10-10): before it, only the
      // UPWARD wiggle shrinks as the trend nears the line (the downward
      // wiggle stays full), so DE stays jagged and never touches early; at
      // the crossing it steps up by CROSS_STEP, and above the line only the
      // DOWNWARD wiggle shrinks, so it never drops back.
      const above = h >= cross;
      if (above) centre = Math.max(centre, limit + CROSS_STEP);
      const room = Math.abs(centre - limit) - 0.01;
      if (above ? n < 0 : n > 0) n *= Math.max(0, Math.min(1, room / cap));
    }
    let v = centre + n;
    if (v < ISO_AB_FLOOR) v = 2 * ISO_AB_FLOOR - v;
    out.push(v);
  }
  return { values: out, cross };
}
const rand = mulberry32(SEED);
const DE_SERIES = hourly(DE_TREND, 0.06, 0.15, rand, ISO_BC);
export const DE_HOURLY = DE_SERIES.values;
export const NDE_HOURLY = hourly(NDE_TREND, 0.045, 0.1, rand, 0).values;
// Dot 1 is DE's first reading past the B/C line (Pete, 2026-10-10).
export const CALL_HOUR = DE_SERIES.cross;
// Narrow charts plot one reading every 4 hours, always keeping the last
// hour (dot 2) — same series, same single crossing.
export const SPARSE_HOURS = Array.from({ length: 217 }, (_, i) => Math.round((i * (HOURS - 1)) / 216));
const ALL_HOURS = Array.from({ length: HOURS }, (_, h) => h);
// Copy — Pete's brief, word for word (2026-10-10). The chart tells one story:
// vibration rises → we notice and call → if ignored, shutdown risk increases.
export const CHART_TAG = 'Example trend.'; // sanctioned disclosure (customerFacingText.test.js)
export const BENCH_LINE = 'Real reading from a 50 HP motor we tested in our shop.';
export const CHART_HEADLINE = 'See problems developing before they stop production.';
export const CHART_SUBTITLE = 'Velocity, DE vs NDE'; // no day count — the axis runs to Day 36 (Pete, 2026-10-10)
export const CALLOUT_CALL = 'Vibration crosses the limit. We call you.';
const CALLOUT_CALL_LINES = ['Vibration crosses the limit.', 'We call you.'];
export const CALLOUT_IGNORED = 'If ignored, shutdown risk increases.';
export const KEY_HOLLOW = 'Shutdown risk rises';
export const CLOSING_LINE = "Don't wait for equipment to shut down to find out something's wrong.";
const CHART_LABEL = 'Example trend: drive-end velocity rising over 30 days to 3.15 mm/s, non-drive-end to 2.85, then continuing to climb. Where drive-end vibration first crosses the limit: vibration crosses the limit, we call you. At the end: if ignored, shutdown risk increases.';

// Hero chart draw-in — Pete's one-time 90-day-rule exception (2026-10-10).
// Once per page load, when the chart scrolls into view: DE and NDE draw left
// to right across the whole chart in one pass (0–3.4 s); dot 1 and its navy
// callout fade in as the line passes the first crossing of 2.8; dot 2 and
// its callout fade in when the line reaches the end (3.4–3.8 s). Then nothing
// moves: no loop, pulse, flashing or replay. Reduced motion (or no
// IntersectionObserver): the finished chart, no animation. Only a clip width
// and opacity change — every element is laid out from the first paint, so
// nothing shifts.
const DRAW = { lines: 3400, ignored: 400 };
export const DRAW_END = DRAW.lines + DRAW.ignored;
// Where dot 1 sits along the plot (0–1); it fades in over the next 4% of the draw.
const CALL_FRAC = CALL_HOUR / (HOURS - 1) + 0.005;
let heroDrawn = false; // once per page load, not once per mount
const clamp01 = (v) => Math.min(1, Math.max(0, v));
const easeInOut = (p) => 0.5 - Math.cos(Math.PI * p) / 2;
export function drawFrame(ms) {
  const reveal = easeInOut(clamp01(ms / DRAW.lines));
  return {
    reveal,
    call: reveal >= 1 ? 1 : clamp01((reveal - CALL_FRAC) / 0.04),
    ignored: clamp01((ms - DRAW.lines) / DRAW.ignored),
  };
}

function prefersReducedMotion() {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
}

/* Runs the draw-in once; `apply(frame)` writes each frame straight to the
   SVG (no re-render per frame). Returns the elapsed-ms ref for render. */
function useDrawIn(targetRef, apply) {
  const elapsed = useRef(null);
  if (elapsed.current === null) {
    const canRun = typeof IntersectionObserver !== 'undefined' && typeof requestAnimationFrame !== 'undefined';
    elapsed.current = heroDrawn || !canRun || prefersReducedMotion() ? DRAW_END : 0;
  }
  const applyRef = useRef(apply);
  applyRef.current = apply;
  useEffect(() => {
    const el = targetRef.current;
    if (!el || elapsed.current >= DRAW_END) return undefined;
    let raf = 0;
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      heroDrawn = true;
      const start = performance.now();
      const tick = () => {
        elapsed.current = Math.min(DRAW_END, performance.now() - start);
        applyRef.current(drawFrame(elapsed.current));
        if (elapsed.current < DRAW_END) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, [targetRef]);
  return elapsed;
}

/* Width of the box the chart sits in, read before paint and on resize. */
function useBoxWidth(fallback) {
  const ref = useRef(null);
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const read = () => { const w = Math.round(el.getBoundingClientRect().width); if (w) setWidth(w); };
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/**
 * The 30-day DE-vs-NDE velocity chart. ISO zone bands for a 50 HP (medium class)
 * motor: good < 1.12, satisfactory 1.12–2.8, unsatisfactory > 2.8 mm/s.
 * Illustration — "how monitoring would have caught it" — NOT data from Job
 * #97818 (a single bench capture). Both traces END at that capture's measured
 * values: DE 3.15, NDE 2.85 mm/s. The dashed marker is the call.
 *
 * Drawn in real pixels at the width it is given (Batch A2, 2026-10-02): the
 * old fixed viewBox scaled every label down with the panel, to ~9–10px. Text
 * here is set in px and stays that size. Below 520px the trace end-labels
 * move out of the chart into a legend underneath instead of shrinking.
 */
/* Width of an SVG <text> (all its lines), measured after fonts load. */
function useTextWidth(ref, fallback, key) {
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof el.getBBox !== 'function') return undefined;
    let live = true;
    const measure = () => { if (!live) return; const n = Math.ceil(el.getBBox().width); if (n) setWidth(n); };
    measure();
    document.fonts?.ready.then(measure);
    return () => { live = false; };
  }, [ref, key]);
  return width;
}

function TrendChart() {
  const [box, w] = useBoxWidth(640);
  // Below 620px dot 2's callout cannot fit; it moves into the key underneath.
  const narrow = w < 620;
  const fs = narrow ? 13 : 14, lead = fs + 5, padX = 12, padY = 9;
  const c1Text = useRef(null), c2Text = useRef(null);
  const c1 = { w: useTextWidth(c1Text, 190, narrow) + 2 * padX, h: 2 * padY + fs + lead - 2 };
  const c2 = { w: useTextWidth(c2Text, 250, narrow) + 2 * padX, h: 2 * padY + fs - 2 };

  // The band above the plot holds one callout: dot 2's on wide charts, dot 1's on phones.
  const L = 48, Rt = 8, T = 8 + (narrow ? c1.h : c2.h) + 14;
  const H = narrow ? 210 : Math.round(Math.min(280, Math.max(230, w * 0.4)));
  const B = T + H;
  const plotRight = w - Rt;
  const x = (i) => L + (i * (plotRight - L)) / (DAYS / 2); // day grid: a tick every 2 days
  const xh = (h) => L + (h * (plotRight - L)) / (HOURS - 1);
  const y = (v) => B - (v / 4) * H;
  // Under ~600px screens (chart narrower than 540) plot every 4th hour.
  const hours = w < 540 ? SPARSE_HOURS : ALL_HOURS;
  const line = (vs) => hours.map((h, i) => `${i ? 'L' : 'M'}${xh(h).toFixed(1)} ${y(vs[h]).toFixed(1)}`).join('');
  const de = line(DE_HOURLY);
  const day30X = x(15);
  // "Day 36" under the last tick only where it clears "Day 30" (not on phones).
  const showLastDay = x(DAYS / 2) - day30X >= 62;
  // Dot 1 sits on a plotted vertex: the first plotted DE reading past 2.8.
  const callH = hours.find((h) => h >= CALL_HOUR);
  const dot1 = { x: xh(callH), y: y(DE_HOURLY[callH]) };
  // Dot 2: where the DE line ends (its last plotted reading).
  const dot2 = { x: xh(HOURS - 1), y: y(DE_HOURLY[HOURS - 1]) };
  // Callout 1 (navy): wide charts — up and left of dot 1, inside the plot,
  // above the DE line (which is under 2.8 there); phones — in the band.
  const c1Box = narrow
    ? { x: Math.max(2, Math.min(w - 2 - c1.w, dot1.x - c1.w * 0.6)), y: 8 }
    : { x: dot1.x - 12 - c1.w, y: Math.max(T + 4, dot1.y - 12 - c1.h) };
  // Callout 2 (outlined, wide charts only): in the band, right-aligned, above dot 2.
  const c2Box = { x: w - 1 - c2.w, y: 8 };
  const t = { fontFamily: F.mono, fontSize: 14 };
  const height = B + 58;

  // Draw-in: refs the animation writes to directly; render sets the same
  // values from the elapsed time, so a resize mid-draw stays consistent.
  const clipRect = useRef(null), callG = useRef(null), ignoredG = useRef(null);
  const legend = useRef(null), keyHollow = useRef(null);
  // Starts 3px left of the axis so no round line cap shows before the draw.
  const revealW = (r) => (r >= 1 ? w : L - 3 + r * (plotRight - L + 6));
  const apply = (f) => {
    clipRect.current?.setAttribute('width', revealW(f.reveal).toFixed(1));
    for (const [r, v] of [[callG, f.call], [legend, f.call], [ignoredG, f.ignored], [keyHollow, f.ignored]]) {
      if (r.current) r.current.style.opacity = v;
    }
  };
  const elapsed = useDrawIn(box, apply);
  const f = drawFrame(elapsed.current);

  return (
    <div ref={box} style={{ width: '100%', minWidth: 0 }}>
      <svg width={w} height={height} viewBox={`0 0 ${w} ${height}`} fill="none" role="img" aria-label={CHART_LABEL} style={{ display: 'block' }}>
        <defs>
          {/* The single sanctioned gradient in the whole system. */}
          <linearGradient id="bs-fillDE" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={C.status.watchStroke} stopOpacity="0.22" />
            <stop offset="1" stopColor={C.status.watchStroke} stopOpacity="0" />
          </linearGradient>
          {/* Draw-in: the traces are revealed left to right by this clip. */}
          <clipPath id="bs-trendReveal">
            <rect ref={clipRect} x="0" y="0" width={revealW(f.reveal).toFixed(1)} height={height} />
          </clipPath>
        </defs>

        <rect x={L} y={T} width={plotRight - L} height={y(2.8) - T} fill={C.zoneBand.unsatisfactory} fillOpacity="0.6" />
        <rect x={L} y={y(2.8)} width={plotRight - L} height={y(1.12) - y(2.8)} fill={C.zoneBand.satisfactory} fillOpacity="0.7" />
        <rect x={L} y={y(1.12)} width={plotRight - L} height={B - y(1.12)} fill={C.zoneBand.good} fillOpacity="0.8" />
        <text x={L + 8} y={T + 19} {...t} fill={C.zoneLabel.unsatisfactory}>C &gt; 2.8</text>
        <text x={L + 8} y={y(2.8) + 20} {...t} fill={C.zoneLabel.satisfactory}>B 1.12–2.8</text>
        <text x={L + 8} y={B - 9} {...t} fill={C.zoneLabel.good}>A &lt; 1.12</text>

        <path d={`M${L} ${T} V${B} H${plotRight}`} stroke={C.ink} strokeOpacity="0.35" />
        <g stroke={C.ink} strokeOpacity="0.2">
          <path d={Array.from({ length: DAYS / 2 }, (_, i) => `M${x(i + 1).toFixed(1)} ${B}v6`).join('')} />
        </g>
        <g {...t} fill={MUTED}>
          <text x={L - 8} y={T + 5} textAnchor="end">4.0</text>
          <text x={L - 8} y={y(2.8) + 5} textAnchor="end">2.8</text>
          <text x={L - 8} y={y(1.12) + 5} textAnchor="end">1.12</text>
          <text x={L - 8} y={B + 5} textAnchor="end">0</text>
          <text x={L} y={B + 24}>Day 1</text>
          <text x={x(7.5)} y={B + 24} textAnchor="middle">Day 15</text>
          <text x={day30X} y={B + 24} textAnchor="end">Day 30</text>
          {showLastDay && <text x={x(DAYS / 2)} y={B + 24} textAnchor="end">Day {DAYS}</text>}
          <text x={0} y={B + 50} fill={C.ink} fontWeight="500">mm/s RMS</text>
        </g>

        <g clipPath="url(#bs-trendReveal)">
          <path d={line(NDE_HOURLY)} stroke={C.link} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          <path d={`${de} L${dot2.x} ${B} L${L} ${B} Z`} fill="url(#bs-fillDE)" />
          <path d={de} stroke={C.status.watchStroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </g>

        {/* Dot 1 — the call: solid, prominent, with the navy callout attached. */}
        <g ref={callG} style={{ opacity: f.call }}>
          {narrow
            ? <path d={`M${dot1.x} ${c1Box.y + c1.h} V${dot1.y - 8}`} stroke={C.ink} strokeWidth="1.5" />
            : <path d={`M${c1Box.x + c1.w - 6} ${c1Box.y + c1.h - 4} L${dot1.x - 5} ${dot1.y - 5}`} stroke={C.ink} strokeWidth="1.5" />}
          <rect x={c1Box.x} y={c1Box.y} width={c1.w} height={c1.h} rx="8" fill={C.ink} />
          <text ref={c1Text} fontFamily={F.body} fontSize={fs} fontWeight="600" fill="#FFFFFF">
            {CALLOUT_CALL_LINES.map((l, i) => <tspan key={l} x={c1Box.x + padX} y={c1Box.y + padY + fs - 2 + i * lead}>{l}</tspan>)}
          </text>
          <circle cx={dot1.x} cy={dot1.y} r="7" fill={C.status.watchStroke} stroke="#FFFFFF" strokeWidth="2.5" />
        </g>

        {/* Dot 2 — if ignored, at the end of the DE line: hollow; its callout outlined, not navy, so dot 1 stays dominant. */}
        <g ref={ignoredG} style={{ opacity: f.ignored }}>
          {!narrow && (
            <>
              <path d={`M${dot2.x} ${c2Box.y + c2.h} V${dot2.y - 7}`} stroke={C.ink} strokeOpacity="0.4" strokeWidth="1.2" />
              <rect x={c2Box.x + 0.5} y={c2Box.y + 0.5} width={c2.w - 1} height={c2.h - 1} rx="8" fill={C.surface} stroke={C.ink} strokeOpacity="0.3" />
              <text ref={c2Text} x={c2Box.x + padX} y={c2Box.y + padY + fs - 2} fontFamily={F.body} fontSize={fs} fontWeight="600" fill={C.ink}>{CALLOUT_IGNORED}</text>
            </>
          )}
          <circle cx={dot2.x} cy={dot2.y} r="6" fill={C.surface} stroke={C.status.watchStroke} strokeWidth="2" />
        </g>
      </svg>
      {/* Key, every width: just "DE" and "NDE", no values — the lines run on
          past the bench values (Pete, 2026-10-10). Phones also explain dot 2 here. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 20px', marginTop: 4 }}>
        <div ref={legend} style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px', ...MONO, fontSize: 15, fontWeight: 500, opacity: f.call }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: C.status.watch }}>
            <span aria-hidden="true" style={{ width: 20, height: 3, borderRadius: 2, background: C.status.watchStroke }} />DE
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: C.link }}>
            <span aria-hidden="true" style={{ width: 20, height: 3, borderRadius: 2, background: C.link }} />NDE
          </span>
        </div>
        {narrow && (
          <span ref={keyHollow} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: F.body, fontSize: 15, fontWeight: 500, color: TEXT, opacity: f.ignored }}>
            <svg aria-hidden="true" width="16" height="16" style={{ flexShrink: 0 }}><circle cx="8" cy="8" r="5.5" fill={C.surface} stroke={C.status.watchStroke} strokeWidth="2" /></svg>{KEY_HOLLOW}
          </span>
        )}
      </div>
      {/* Pete's copy, word for word (2026-10-10). */}
      <p style={{ margin: '10px 0 0', fontFamily: F.body, fontSize: narrow ? 15 : 14, fontWeight: 500, lineHeight: 1.4, color: TEXT }}>{CLOSING_LINE}</p>
    </div>
  );
}

/* ── THE LOOP ────────────────────────────────────────────────────────────── */
const LOOP_STEPS = [
  {
    title: 'Reading',
    body: 'Sensors on the bearing housings report vibration and temperature every hour.',
    icon: <path d="M3 12h3l2-6 4 12 3-8 2 2h4" />,
  },
  {
    title: 'Grade',
    body: 'Every asset gets one letter against ISO 20816 for its size class. No jargon to decode.',
    letter: 'C',
  },
  {
    title: 'A call',
    body: 'We review readings every business day. If equipment crosses a limit, we call the person who runs the plant — typically the same business day. A phone call, not a dashboard alert.',
    icon: <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" />,
    dark: true,
  },
  {
    title: 'Repair, if needed',
    body: 'If an inspection finds a problem, the equipment comes out on your schedule.',
    icon: <path d="M14 4a4 4 0 0 0-3.5 6L4 16.5 6.5 19 13 12.5A4 4 0 0 0 19 9l-2.5 1-1.5-1.5 1-2.5z" />,
  },
  {
    title: 'On record',
    body: 'The teardown shows what was actually wrong, and that finding is recorded against the readings.',
    icon: <><circle cx="12" cy="12" r="9" /><path d="M8 12.5l3 3 5-6" /></>,
    check: true,
  },
];

function TheLoop() {
  return (
    <div id="how" className="bs-shell" style={{ position: 'relative', paddingTop: 56, paddingBottom: 80, display: 'flex', flexDirection: 'column', gap: 32 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 760 }}>
        <div style={{ ...MONO, fontSize: 14, fontWeight: 500, letterSpacing: '0.06em', color: C.link }}>WHAT HAPPENS WHEN A READING MOVES</div>
        <h2 style={{ margin: 0, ...DISP, fontWeight: 700, fontSize: 38, letterSpacing: F.tracking.tight, color: C.ink }}>
          A reading, a grade, a call — and a record.
        </h2>
      </div>
      <div className="bs-loop">
        <svg className="bs-loop-rail" aria-hidden="true" style={{ position: 'absolute', left: 0, top: 44, width: '100%', height: 2 }} viewBox="0 0 100 2" preserveAspectRatio="none">
          <path d="M0 1H100" stroke={C.ink} strokeOpacity="0.18" strokeWidth="2" strokeDasharray="1 0.6" />
        </svg>
        {LOOP_STEPS.map((s, i) => (
          <div key={s.title} className="bs-loop-step" style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingRight: i < LOOP_STEPS.length - 1 ? 24 : 0, position: 'relative' }}>
            <div className="bs-loop-circle" style={{
              width: 88, height: 88, borderRadius: '50%', flexShrink: 0,
              background: s.dark ? C.ink : C.surface,
              border: s.dark ? 'none' : `1.5px solid rgba(10,27,59,0.18)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {s.letter ? (
                <span style={{ ...DISP, fontWeight: 800, fontSize: 30, color: C.status.watch }}>{s.letter}</span>
              ) : (
                <svg width={s.check ? 40 : 38} height={s.check ? 40 : 38} viewBox="0 0 24 24" fill="none"
                     stroke={s.dark ? '#FFFFFF' : s.check ? C.status.good : C.ink}
                     strokeWidth={s.check ? 1.8 : 1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  {s.icon}
                </svg>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
              <div style={{ ...DISP, fontWeight: 700, fontSize: 20, color: C.ink }}>{s.title}</div>
              <div style={{ fontSize: 16, lineHeight: 1.55, color: TEXT }}>{s.body}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── SERVICES ────────────────────────────────────────────────────────────── */
function Services() {
  return (
    <div id="screening" className="bs-shell bs-services" style={{ position: 'relative', paddingBottom: 80 }}>
      <div className="bs-service" style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 32, borderRadius: R['2xl'], background: C.surface, border: `1px solid ${C.hairline}`, boxShadow: tokens.shadow.md }}>
        <div style={{ ...MONO, fontSize: 14, fontWeight: 500, letterSpacing: '0.06em', color: C.link }}>01 · ONE VISIT</div>
        <div style={{ ...DISP, fontWeight: 700, fontSize: 24, color: C.ink }}>Equipment screening</div>
        <div style={{ fontSize: 18, lineHeight: 1.6, color: TEXT }}>
          We come to your plant, take vibration and thermal readings on up to 10 assets, and hand you a graded report the same week. A paid, one-time visit. No install, no ongoing commitment.
        </div>
      </div>

      <div id="monitoring" className="bs-service" style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 32, borderRadius: R['2xl'], background: C.ink, color: '#FFFFFF', boxShadow: tokens.shadow.lg }}>
        <div style={{ ...MONO, fontSize: 14, fontWeight: 500, letterSpacing: '0.06em', color: C.accent }}>02 · EVERY HOUR</div>
        <div style={{ ...DISP, fontWeight: 700, fontSize: 24 }}>Monitoring on the equipment that stops the line</div>
        <div style={{ fontSize: 18, lineHeight: 1.6, color: C.textOnDarkSecondary }}>
          Sensors stay on your critical equipment and report hourly. You get each asset's grade and trend, a weekly report — a one-page summary with supporting detail — and a call from Byrdsense — typically the same business day — if equipment crosses a limit.
        </div>
      </div>

      <div className="bs-service" style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 32, borderRadius: R['2xl'], background: C.surface, border: `1px solid ${C.hairline}`, boxShadow: tokens.shadow.md }}>
        <div style={{ ...MONO, fontSize: 14, fontWeight: 500, letterSpacing: '0.06em', color: C.link }}>03 · WHEN IT COUNTS</div>
        <div style={{ ...DISP, fontWeight: 700, fontSize: 24, color: C.ink }}>Readings tied to repairs</div>
        <div style={{ fontSize: 18, lineHeight: 1.6, color: TEXT }}>
          When equipment comes out for repair, what the teardown finds is recorded against its readings, so the reading and the repair stay one record.
        </div>
      </div>
    </div>
  );
}

/* ── SERVICE SPEC ────────────────────────────────────────────────────────── */
// Outside review #3 (Pete, 2026-10-09): a plain spec instead of feature pills.
// Pete's copy, word for word (HomePage.serviceSpec.test.js). No prices.
export const SERVICE_SPEC = [
  ['Readings', 'every hour, from a wireless sensor on each asset'],
  ['Review', 'every business day, by our team'],
  ['When equipment crosses a limit', 'we call you, typically the same business day'],
  ['Weekly report', 'every asset graded A–F, with a dated action for any that need attention'],
  ['Contract', 'month-to-month, or a lower rate paid annually'],
  ['Hardware', 'sensors and gateway stay ours and are included in the monthly price'],
  ['Connection', 'the gateway is designed to run on its own cellular connection'],
  ['Not included', "after-hours analyst coverage. Readings show how much the equipment is vibrating. They don't name a failed part or say how long it will last."],
];

function ServiceSpec() {
  return (
    <section aria-labelledby="bs-spec-title" className="bs-shell" style={{ position: 'relative', paddingBottom: 80 }}>
      <div style={{ padding: 32, borderRadius: R['2xl'], background: C.surface, border: `1px solid ${C.hairline}` }}>
        <div style={{ ...MONO, fontSize: 14, fontWeight: 500, letterSpacing: '0.06em', color: C.link }}>THE DETAILS</div>
        <h2 id="bs-spec-title" style={{ margin: '8px 0 20px', ...DISP, fontWeight: 700, fontSize: 28, letterSpacing: F.tracking.tight, color: C.ink }}>
          Monitoring service
        </h2>
        <dl className="bs-spec" style={{ margin: 0 }}>
          {SERVICE_SPEC.map(([term, def]) => (
            <div key={term} className="bs-spec-row" style={{ borderTop: `1px solid ${C.hairline}` }}>
              <dt style={{ ...MONO, fontSize: 14, fontWeight: 500, color: C.ink }}>{term}</dt>
              <dd style={{ margin: 0, fontSize: 18, lineHeight: 1.55, color: TEXT }}>{def}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

/* ── REPORT PREVIEW ──────────────────────────────────────────────────────── */
// Copied from the live sample screening PDF
// (public/samples/byrdsense-sample-screening-report.pdf, sha 96ac5f8d… — the
// redesigned report, rendered locally from feat/report-redesign fcc4935 on
// 2026-10-02): name, mm/s, °F, grade, the motor's HP and the action, exactly
// as page 1 prints them. Each grade is locked against our grading at THAT
// motor's own HP (HomePage.grades.test.js) — a 15 HP motor is the small class,
// where 5.4 mm/s is D. The deadlines are dated from the sample's screening
// date, so that date is printed above the rows (PREVIEW_DATE); re-cut the PDF
// and these rows together. The A motor has no action cell in the PDF — it is
// listed under "No additional action indicated".
export const PREVIEW_DATE = 'September 28, 2026';
export const PREVIEW_ROWS = [
  { name: 'Chiller Pump #2',          hp: 15, velocity: '5.4',  tempF: '142', action: 'Complete inspection by October 12, 2026', g: 'D' },
  { name: 'Dust Collector Fan',       hp: 25, velocity: '3.1',  tempF: '131', action: 'Re-check by October 28, 2026',             g: 'C' },
  { name: 'Main Line Air Compressor', hp: 50, velocity: '1.02', tempF: '118', action: 'No additional action indicated',          g: 'A' },
];

function ReportPreview() {
  return (
    <div id="case" className="bs-shell" style={{ position: 'relative', paddingBottom: 80 }}>
      <div className="bs-report" style={{ padding: '44px 48px', borderRadius: R['3xl'], background: C.surface, border: `1px solid ${C.hairline}` }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ ...MONO, fontSize: 14, fontWeight: 500, letterSpacing: '0.06em', color: C.link }}>THE REPORT</div>
          <h3 style={{ margin: 0, ...DISP, fontWeight: 800, fontSize: 32, letterSpacing: F.tracking.tight, lineHeight: 1.1, color: C.ink }}>
            A one-page summary a plant manager can read in a minute, with the detail behind it.
          </h3>
          <p style={{ margin: 0, fontSize: 18, lineHeight: 1.6, color: TEXT, maxWidth: 580 }}>
            Every asset, one letter, the reading it earned, and what to do about it with a date. Written the way a foreman would say it.
          </p>
          <a href="/samples/byrdsense-sample-screening-report.pdf" target="_blank" rel="noopener"
             style={{ alignSelf: 'flex-start', marginTop: 6, color: C.link, fontWeight: 600, fontSize: 18, textDecoration: 'none' }}>
            See a sample report (PDF) →
          </a>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 18, borderRadius: R.lg, background: C.ground, border: `1px solid ${C.hairlineSoft}` }}>
          {/* The rows' deadlines are dated from this screening date. */}
          <div style={{ ...MONO, fontSize: 14, fontWeight: 500, color: MUTED, padding: '0 2px 2px' }}>Sample screening · <span style={{ whiteSpace: 'nowrap' }}>{PREVIEW_DATE}</span></div>
          {PREVIEW_ROWS.map((r) => {
            const g = tokens.grade[r.g];
            return (
              <div key={r.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: R.md, background: C.surface, borderLeft: `4px solid ${g.stroke}` }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 18, lineHeight: 1.3, color: C.ink }}>{r.name}</div>
                  <div style={{ ...MONO, fontSize: 16, color: C.ink }}>{r.velocity} <span style={{ color: MUTED }}>mm/s</span> · {r.tempF}<span style={{ color: MUTED }}>°F</span></div>
                  <div style={{ fontSize: 16, lineHeight: 1.4, color: TEXT }}>{r.action}</div>
                </div>
                {/* 20px bold = WCAG large text: the D and B chip colours (4.4:1, 4.2:1) pass at 3:1. */}
                <div style={{ width: 40, height: 40, flexShrink: 0, borderRadius: R.base, background: g.bg, color: g.fg, ...DISP, fontWeight: 800, fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{r.g}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ── REQUEST ─────────────────────────────────────────────────────────────── */
function RequestBand() {
  // Submit logic and endpoint unchanged — success only on HTTP 200, no mailto fallback.
  const [form, setForm] = useState({ name: '', company: '', phone: '', motors: '' });
  const [status, setStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch(CONTACT_API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (res.ok) { setStatus('sent'); }
      else { setStatus('error'); }
    } catch {
      setStatus('error');
    }
  };

  const inputStyle = {
    height: 50, padding: '0 14px', borderRadius: R.base,
    border: `1px solid rgba(120,183,255,0.3)`, background: C.inkField,
    color: '#FFFFFF', fontSize: 17, fontFamily: F.body, width: '100%', boxSizing: 'border-box',
  };
  const labelStyle = { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 16, fontWeight: 500, color: C.textOnDarkSecondary };

  return (
    <div className="bs-shell" style={{ position: 'relative' }}>
      <div id="request" className="bs-request" style={{ padding: '56px 48px', borderRadius: `${R['3xl']} ${R['3xl']} 0 0`, background: C.ink, color: '#FFFFFF' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ ...MONO, fontSize: 14, fontWeight: 500, letterSpacing: '0.06em', color: C.accent }}>GET STARTED</div>
          <h3 style={{ margin: 0, ...DISP, fontWeight: 800, fontSize: 34, letterSpacing: F.tracking.tight }}>Request a screening.</h3>
          <p style={{ margin: 0, fontSize: 18, lineHeight: 1.6, color: C.textOnDarkSecondary, maxWidth: 560 }}>
            Tell us about your plant. We'll schedule a visit, grade your equipment, and send the report.
          </p>

          {/* What a screening actually is, before anyone fills in the form.
              No dollar figure here — screenings are quoted per site. */}
          <ul style={{ margin: '6px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 9, maxWidth: 560 }}>
            {[
              'A paid, one-time visit.',
              'Up to 10 assets, graded A–F with a report you keep.',
              'Southeast Tennessee and North Georgia.',
              'We call you within one business day to set the date.',
            ].map((t) => (
              <li key={t} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 17, lineHeight: 1.5, color: C.textOnDarkSecondary }}>
                <span aria-hidden="true" style={{ color: C.accent, lineHeight: 1.5 }}>—</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>

          <div style={{ fontSize: 17, color: C.textOnDarkSecondary, paddingTop: 10 }}>
            Or call: <a href="tel:4237904745" style={{ ...MONO, fontWeight: 500, color: C.accent, textDecoration: 'none' }}>(423) 790-4745</a>
          </div>
        </div>

        {status === 'sent' ? (
          <div style={{ background: 'rgba(120,183,255,0.12)', borderRadius: R.md, padding: '32px 24px' }}>
            <div style={{ ...DISP, fontWeight: 700, fontSize: 20, marginBottom: 8 }}>Request received.</div>
            <div style={{ fontSize: 17, lineHeight: 1.5, color: C.textOnDarkSecondary }}>We'll be in touch within one business day to schedule your screening.</div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <label style={labelStyle}>Your name
              <input type="text" required placeholder="Your name" value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })} style={inputStyle} />
            </label>
            <label style={labelStyle}>Company
              <input type="text" required placeholder="Company" value={form.company}
                     onChange={(e) => setForm({ ...form, company: e.target.value })} style={inputStyle} />
            </label>
            <label style={labelStyle}>Phone
              <input type="tel" required placeholder="Phone number" value={form.phone}
                     onChange={(e) => setForm({ ...form, phone: e.target.value })} style={inputStyle} />
            </label>
            <button type="submit" disabled={status === 'sending'}
                    style={{ height: 54, border: 0, borderRadius: R.md, background: C.primary, color: C.ink, fontWeight: 700, fontSize: 17, marginTop: 6, cursor: status === 'sending' ? 'default' : 'pointer', fontFamily: F.body, opacity: status === 'sending' ? 0.75 : 1 }}>
              {status === 'sending' ? 'Sending…' : 'Request my screening'}
            </button>
            <div style={{ fontSize: 16, lineHeight: 1.5, color: C.textOnDarkSecondary }}>
              We'll only use this to call you about a screening.{' '}
              <a href="/privacy" style={{ color: C.accent }}>Privacy policy</a>
            </div>
            {status === 'error' && (
              <div role="alert" style={{ fontSize: 16, lineHeight: 1.5, color: '#FFD9CE', background: 'rgba(240,86,60,0.18)', borderRadius: R.sm, padding: '12px 14px' }}>
                That didn't go through. Call <a href="tel:4237904745" style={{ color: C.accent }}>(423) 790-4745</a> and we'll get you on the schedule.
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}

/* ── FOOTER ──────────────────────────────────────────────────────────────── */
function SiteFooter() {
  const link = { color: C.textOnDarkMuted, textDecoration: 'none' };
  return (
    <footer style={{ position: 'relative', background: C.ink, color: '#FFFFFF' }}>
      <div className="bs-shell" style={{ paddingTop: 32, paddingBottom: 40, borderTop: `1px solid rgba(120,183,255,0.18)`, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <img src={WORDMARK_ON_NAVY} alt="Byrdsense" style={{ width: 150, height: 'auto', display: 'block' }} />
          <div style={{ fontSize: 15, color: C.textOnDarkSecondary }}>{noOrphan(TAGLINE)}</div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 24px', alignItems: 'center', fontSize: 16 }}>
          <a href="tel:4237904745" style={{ ...link, ...MONO, color: C.accent }}>(423) 790-4745</a>
          <a href="mailto:info@byrdsense.com" style={{ ...link, color: C.accent }}>info@byrdsense.com</a>
          <a href="/privacy" style={link}>Privacy</a>
          <a href="/terms" style={link}>Terms</a>
        </div>
      </div>
    </footer>
  );
}
